import type { Cents } from '@keel/core';
import { TrendingDown, TrendingUp, Minus } from 'lucide-react';
import { forwardRef, type HTMLAttributes } from 'react';
import { cn } from '@/lib/cn';
import { useMoney } from '@/lib/money';

export interface MoneyTextProps extends Omit<HTMLAttributes<HTMLSpanElement>, 'children'> {
  readonly cents: Cents;
  /** Defaults to the current entity currency. */
  readonly currency?: string;
  /** `always` prefixes + on positives. */
  readonly sign?: 'auto' | 'always' | 'never';
  /** Colour by sign: `auto` = green when positive & red when negative; `negative` = only reds. Default none. */
  readonly tone?: 'none' | 'auto' | 'negative';
  /** 12.3k style. */
  readonly compact?: boolean;
  /** Override the global privacy blur (e.g. never blur a percentage-only figure). */
  readonly blur?: boolean;
}

/**
 * Formatted money with tabular figures, optional sign colouring and privacy blur.
 * Figures stay in layout when blurred (no shift) and are marked `data-private` for screenshots/recordings.
 */
export const MoneyText = forwardRef<HTMLSpanElement, MoneyTextProps>(
  ({ cents, currency, sign = 'auto', tone = 'none', compact, blur, className, ...props }, ref) => {
    const money = useMoney();
    const blurred = blur ?? money.blur;
    const text = money.format(cents, { currency, sign, compact });
    return (
      <span
        ref={ref}
        data-private={blurred || undefined}
        className={cn(
          'tabular-nums',
          tone === 'auto' && cents > 0 && 'text-positive',
          (tone === 'auto' || tone === 'negative') && cents < 0 && 'text-negative',
          blurred && 'privacy-blur',
          className,
        )}
        title={compact && !blurred ? money.format(cents, { currency, sign }) : undefined}
        {...props}
      >
        {text}
      </span>
    );
  },
);
MoneyText.displayName = 'MoneyText';

export interface DeltaBadgeProps {
  /** Change in minor units (or use `pct`). */
  readonly cents?: Cents;
  /** Percentage change, e.g. 4.2 for +4.2 %. */
  readonly pct?: number;
  /** Period description for screen readers, e.g. "over 30 days". */
  readonly period?: string;
  /** When up is bad (spending), invert the colours. */
  readonly inverse?: boolean;
  readonly className?: string;
}

/** Up/down change chip with icon + colour + spoken text (never colour alone). */
export const DeltaBadge = ({ cents, pct, period, inverse, className }: DeltaBadgeProps) => {
  const money = useMoney();
  const v = cents ?? pct ?? 0;
  const dir = v > 0 ? 'up' : v < 0 ? 'down' : 'flat';
  const good = dir === 'flat' ? null : (dir === 'up') !== Boolean(inverse);
  const Icon = dir === 'up' ? TrendingUp : dir === 'down' ? TrendingDown : Minus;
  const magnitude =
    cents !== undefined ? money.format(Math.abs(cents)) : `${Math.abs(pct ?? 0).toLocaleString(money.locale, { maximumFractionDigits: 1 })}%`;
  const spoken = dir === 'flat' ? `No change${period ? ` ${period}` : ''}` : `${dir === 'up' ? 'Up' : 'Down'} ${magnitude}${period ? ` ${period}` : ''}`;
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium tabular-nums',
        good === null && 'bg-surface-sunken text-text-muted',
        good === true && 'bg-positive-soft text-positive',
        good === false && 'bg-negative-soft text-negative',
        className,
      )}
    >
      <Icon className="size-3.5" aria-hidden />
      <span aria-hidden className={cn(cents !== undefined && money.blur && 'privacy-blur')}>
        {magnitude}
      </span>
      <span className="sr-only">{spoken}</span>
    </span>
  );
};
